"use client";

import { Settings2 } from "lucide-react";
import { cn } from "../../../lib/utils";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../dropdown-menu";
import { menuTriggerClass } from "../trigger-recipes";
import type { DataTableViewOptionsProps } from "./types";

/**
 * Job C — ACT, pick-many subset (`docs/picker-taxonomy.md` §1.4, §2.7, §3.3).
 *
 * **The accessibility fix this carries.** Each row was a `DropdownMenuItem`
 * (`role="menuitem"`) with a hand-drawn `<span aria-hidden>✓</span>` box. A
 * `menuitem` has no checked state in ARIA, so a screen reader announced eight
 * identical "Status, menu item" rows for a control whose entire purpose is
 * eight independent toggles, and the only signal that a column was visible was
 * the `aria-hidden` glyph — invisible to assistive tech and carried by colour
 * alone. `DropdownMenuCheckboxItem` is `role="menuitemcheckbox"` with real
 * `aria-checked`, announced as "checked"/"not checked", and Radix keeps the
 * menu open on toggle so several columns can be flipped in one visit.
 *
 * The trigger adopts §2.7 rather than `Button variant="ghost"`, which supplies
 * the `data-[state=open]` pair the old string lacked — the affordance no longer
 * renders at rest while its own panel is open.
 */

export function DataTableViewOptions<TData>({
  table,
  className,
}: DataTableViewOptionsProps<TData>) {
  const toggleableColumns = table
    .getAllColumns()
    .filter(
      (column) =>
        typeof column.accessorFn !== "undefined" && column.getCanHide(),
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={menuTriggerClass(
            "compact",
            cn("ml-auto hidden md:inline-flex", className),
          )}
        >
          <Settings2 aria-hidden="true" />
          View
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {toggleableColumns.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.id}
            className="capitalize"
            checked={column.getIsVisible()}
            /* Pick-many: hold the menu open across successive toggles, the same
             * contract the feature filter menus use (\u00a71.4). Without this Radix
             * dismisses on the first toggle and hiding three columns costs
             * three round-trips through the trigger. */
            onSelect={(event) => event.preventDefault()}
            onCheckedChange={(checked) => column.toggleVisibility(checked)}
          >
            {column.id}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

DataTableViewOptions.displayName = "DataTableViewOptions";
